import {
	type TouchEventHandler,
	type UIEventHandler,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
} from 'react'
import { getContainer } from '../../utils/getContainer'

// Configuration constants for drag behavior
const MODAL_SIZE_PERCENTAGE = 0.2 // 20% of modal height threshold to close the bottom sheet
const BACKDROP_OPACITY = '--backdrop-opacity' // CSS custom property for backdrop opacity

/**
 * Determines if the user is dragging on scrollable content
 * This prevents interfering with native scroll behavior
 */
const isDraggingOnScrollableContent = (target: HTMLElement) => {
	const found = target?.closest('[data-type="content"]')
	if (!found) {
		return false
	}
	// Compare client height with scroll height to detect scrollable content
	return found.clientHeight !== found.scrollHeight
}

/**
 * Custom hook that enables draggable behavior for bottom sheet dialogs
 * Handles touch events to allow users to drag the sheet up/down and close it
 */
export function useDraggableBottomSheet({
	dialogRef,
	draggableRef,
	opened,
	setOpened,
}: {
	dialogRef: React.RefObject<HTMLDialogElement | null>
	draggableRef: React.RefObject<HTMLDivElement | null>
	opened: boolean
	setOpened: (opened: boolean) => void
}) {
	// Stores initial drag state (touch offset and sheet height)
	const initialState = useRef({
		offset: 0,
		height: 0,
	})

	// Tracks whether global scroll should be locked while dragging to close
	const scrollLockRef = useRef(false)

	// Check if user is currently dragging (height > 0 indicates active drag)
	const isDragging = useCallback(() => !!initialState.current.height, [])

	/**
	 * Determines if the content is in a scrolling state
	 * When content is scrollable, dragging is only allowed from the header
	 * This prevents conflicts between scroll and drag gestures
	 */
	const isContentScrolling = useCallback(
		(target: HTMLElement) =>
			draggableRef.current?.querySelector('[data-scrolling]') &&
			!target.closest('[data-dialog-header]'),
		[draggableRef]
	)

	/**
	 * Handles the dragging motion during touch move events
	 * Updates the sheet position and backdrop opacity in real-time
	 */
	const onTouchMove = useCallback<TouchEventHandler<HTMLElement>>(
		(e) => {
			// Preserve native scroll behavior on scrollable content
			// The CSS property overscroll-behavior: contain helps with this
			if (
				e.cancelable &&
				!isDraggingOnScrollableContent(e.target as HTMLElement)
			) {
				e.preventDefault()
			}
			const draggable = draggableRef.current
			if (
				isDragging() &&
				!isContentScrolling(e.target as HTMLElement) &&
				draggable
			) {
				const { offset, height } = initialState.current
				// Calculate the minimum top position (fully opened state)
				const topLimit = window.innerHeight - height
				// Calculate current top position based on touch position
				const top = Math.max(e.touches[0].clientY - offset, topLimit)
				// Calculate backdrop opacity percentage (0-1) based on drag distance
				const percentage = 1 - ((topLimit - top) * -1) / height

				// Update backdrop opacity to create fade effect while dragging
				if (dialogRef.current) {
					dialogRef.current.style.setProperty(BACKDROP_OPACITY, `${percentage}`)
				}
				// Store drag distance
				draggable.dataset.distance = `${(topLimit - top) * -1}`
				// Update sheet position in real-time
				getContainer(dialogRef.current)?.style.setProperty('top', `${top}px`)
			}
		},
		[dialogRef, draggableRef, isContentScrolling, isDragging]
	)

	/**
	 * Handles the end of drag gesture (touchend event)
	 * Determines whether to close the sheet or snap it back to open position
	 * Based on drag distance only
	 */
	const onTouchEnd = useCallback<TouchEventHandler<HTMLElement>>(
		(e) => {
			if (isDragging()) {
				const { height } = initialState.current
				const draggable = draggableRef.current
				if (!isContentScrolling(e.target as HTMLElement) && draggable) {
					const distance = Number.parseInt(
						draggable?.dataset.distance ?? '0',
						10
					)

					// Threshold for closing based on drag distance (20% of modal height)
					const shouldCloseHeight = height * MODAL_SIZE_PERCENTAGE

					// Close sheet if dragged far enough
					if (distance >= shouldCloseHeight) {
						setOpened(false)
						draggable.dataset.distance = '-1'
					} else {
						// Snap back to open position - remove temporary positioning
						getContainer(dialogRef.current)?.style.removeProperty('top')
						dialogRef.current?.style.removeProperty(BACKDROP_OPACITY)
					}
				}
				// Handle edge case: restore position when scrolling content while dragging
				if (isContentScrolling(e.target as HTMLElement) && draggable) {
					const topLimit = window.innerHeight - height
					draggable.dataset.distance = `${topLimit}`
					getContainer(dialogRef.current)?.style.setProperty(
						'top',
						`${topLimit}px`
					)
					dialogRef.current?.style.removeProperty(BACKDROP_OPACITY)
				}
			}
			scrollLockRef.current = false
		},
		[dialogRef, draggableRef, isContentScrolling, isDragging, setOpened]
	)
	/**
	 * Initializes drag state when touch starts
	 * Records initial touch position and sheet dimensions for drag calculations
	 */
	const onTouchStart = useCallback<TouchEventHandler<HTMLElement>>(
		(e) => {
			if (!isContentScrolling(e.target as HTMLElement)) {
				const height = dialogRef.current?.children[0].clientHeight ?? 0

				// Calculate initial offset from touch position to sheet bottom
				const bottom = (window.innerHeight - height - e.touches[0].clientY) * -1

				// Only initialize drag state if content is at top (not scrolled)
				if (draggableRef.current?.scrollTop === 0) {
					draggableRef.current.dataset.distance = '0'
					initialState.current = {
						offset: bottom, // Distance from touch point to sheet edge
						height, // Sheet height for percentage calculations
					}
					scrollLockRef.current = true
				}
			}
		},
		[dialogRef, draggableRef, isContentScrolling]
	)

	const onScroll = useCallback<UIEventHandler<HTMLElement>>(
		(e) => e.preventDefault(),
		[]
	)

	const onTouchCancel = useCallback<TouchEventHandler<HTMLElement>>(() => {
		scrollLockRef.current = false
	}, [])

	/**
	 * Effects to manage touch event listeners on the dialog element
	 * Sets up/tears down dragging behavior when sheet opens/closes
	 */
	useEffect(() => {
		// Reset position when sheet is closed
		if (!opened) {
			scrollLockRef.current = false
			getContainer(dialogRef.current)?.style.removeProperty('top')
		}
	}, [dialogRef, opened])

	useEffect(() => {
		// Reset drag state when sheet opens
		if (opened) {
			initialState.current = {
				offset: 0,
				height: 0,
			}
		}
	}, [opened])

	useLayoutEffect(() => {
		// Cleanup function to remove event listeners and reset styles
		return () => {
			dialogRef.current?.style.removeProperty(BACKDROP_OPACITY)
		}
	}, [dialogRef])

	return useMemo(
		() => ({ onTouchStart, onTouchMove, onTouchEnd, onScroll, onTouchCancel }),
		[onTouchStart, onTouchMove, onTouchEnd, onScroll, onTouchCancel]
	)
}
